/**
 * CanonicalHostRegistry
 *
 * Maps overlay surfaces to their canonical host page/tab. When an overlay
 * closes and its live parent is no longer mounted (e.g. user navigated away
 * while a deep-linked drawer was open), the orchestrator uses this registry
 * to resolve a deterministic fallback route.
 *
 * Registry entries are static configuration — they never change at runtime.
 * Pages register their canonical hosts at import time.
 *
 * Highlight patterns: Each entry can specify a highlightPattern that tells
 * the focus restoration system how to find the trigger element on the host
 * page. Pattern uses {entityId} as a placeholder.
 *
 * @module lib/navigation/canonicalHostRegistry
 */

import type { CanonicalHostEntry } from './types';

// ── Registry ──────────────────────────────────────────────────────────────
const registry = new Map<string, CanonicalHostEntry>();

function entryKey(kind: string, name: string): string {
    return `${kind}:${name}`;
}

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Register a canonical host for an overlay surface.
 */
export function registerCanonicalHost(entry: CanonicalHostEntry): void {
    const key = entryKey(entry.overlaySurface.kind, entry.overlaySurface.name);
    registry.set(key, entry);
}

/**
 * Resolve the canonical host for an overlay surface.
 * Returns null if no host is registered.
 */
export function resolveCanonicalHost(
    kind: string,
    name: string,
): CanonicalHostEntry | null {
    return registry.get(entryKey(kind, name)) ?? null;
}

/**
 * Resolve host route with parameter substitution.
 *
 * @example
 * resolveHostRoute('drawer', 'milestone-drawer', { projectId: 'p-123' })
 * // => '/projects/p-123?tab=milestones'
 */
export function resolveHostRoute(
    kind: string,
    name: string,
    routeParams?: Record<string, string>,
): string | null {
    const entry = resolveCanonicalHost(kind, name);
    if (!entry) return null;

    let route = entry.hostRoute;

    // Substitute :param placeholders
    if (routeParams) {
        for (const [param, value] of Object.entries(routeParams)) {
            route = route.replace(`:${param}`, encodeURIComponent(value));
        }
    }

    // Append host params as search string
    if (entry.hostParams && Object.keys(entry.hostParams).length > 0) {
        const search = new URLSearchParams(entry.hostParams).toString();
        route += `?${search}`;
    }

    return route;
}

/**
 * Resolve the highlight target element id for a given overlay's entity.
 * Substitutes {entityId} in the highlight pattern.
 *
 * @example
 * resolveHighlightTarget('drawer', 'milestone-drawer', 'ms-123')
 * // => 'milestone-ms-123'
 */
export function resolveHighlightTarget(
    kind: string,
    name: string,
    entityId: string,
): string | null {
    const entry = resolveCanonicalHost(kind, name);
    if (!entry?.highlightPattern) return null;
    return entry.highlightPattern.replace('{entityId}', entityId);
}

/**
 * Batch-register multiple canonical hosts. Called once at app startup.
 */
export function registerCanonicalHosts(entries: CanonicalHostEntry[]): void {
    entries.forEach(registerCanonicalHost);
}

// ── Default registrations ─────────────────────────────────────────────────
// These are the known AX1 overlay → host mappings.

registerCanonicalHosts([
    // ─── Milestones ────────────────────────────────────────
    {
        overlaySurface: { kind: 'drawer', name: 'milestone-drawer' },
        hostRoute: '/projects/:projectId',
        hostParams: { tab: 'milestones' },
        highlightPattern: 'milestone-{entityId}',
    },
    // ─── Capital ───────────────────────────────────────────
    {
        overlaySurface: { kind: 'drawer', name: 'capital-manage-drawer' },
        hostRoute: '/projects/:projectId',
        hostParams: { tab: 'capital' },
        highlightPattern: 'milestone-{entityId}',
    },
    {
        overlaySurface: { kind: 'drawer', name: 'gate-detail-drawer' },
        hostRoute: '/projects/:projectId',
        hostParams: { tab: 'capital' },
        highlightPattern: 'gate-{entityId}',
    },
    {
        overlaySurface: { kind: 'drawer', name: 'settlement-detail-drawer' },
        hostRoute: '/projects/:projectId',
        hostParams: { tab: 'capital' },
        highlightPattern: 'settlement-{entityId}',
    },
    // ─── Team / Users ──────────────────────────────────────
    {
        overlaySurface: { kind: 'drawer', name: 'user-inspector-drawer' },
        hostRoute: '/users',
        highlightPattern: 'user-{entityId}',
    },
    {
        overlaySurface: { kind: 'drawer', name: 'team-management-drawer' },
        hostRoute: '/projects/:projectId',
        hostParams: { tab: 'team' },
    },
    // ─── Project CRUD ──────────────────────────────────────
    {
        overlaySurface: { kind: 'drawer', name: 'create-project-drawer' },
        hostRoute: '/projects',
    },
    {
        overlaySurface: { kind: 'drawer', name: 'create-task-drawer' },
        hostRoute: '/projects/:projectId',
        hostParams: { tab: 'tasks' },
    },
    // ─── Modals ────────────────────────────────────────────
    {
        overlaySurface: { kind: 'modal', name: 'evidence-modal' },
        hostRoute: '/projects/:projectId',
        hostParams: { tab: 'capital' },
        highlightPattern: 'evidence-{entityId}',
    },
    {
        overlaySurface: { kind: 'modal', name: 'delete-confirmation-modal' },
        hostRoute: '/projects/:projectId',
    },
    {
        overlaySurface: { kind: 'modal', name: 'share-evidence-pack-modal' },
        hostRoute: '/projects/:projectId',
        hostParams: { tab: 'capital' },
    },
]);
